/**
 * Macro proportion visualization (spec section 10).
 *
 * Uses the validated categorical slots 1–3. Every segment carries a visible
 * direct label, which satisfies the relief rule for the light-mode aqua step and
 * means identity never depends on colour alone.
 *
 * Section 10 also requires that an incomplete macro set is not presented as a
 * complete breakdown. When a macro is missing, the remainder is drawn as an
 * explicit "unaccounted" segment and called out in words.
 */

import type { MacroSplit } from '@/lib/nutrition/calculations';
import { formatPercent } from '@/lib/format';

interface Props {
  macros: MacroSplit;
  /** Hide the legend in dense contexts like list cards. */
  compact?: boolean;
}

export function MacroBar({ macros, compact = false }: Props): React.JSX.Element {
  const segments = [
    { key: 'protein', label: 'Protein', value: macros.proteinPct.value, className: 'macro-seg-protein', color: 'var(--series-protein)' },
    { key: 'carbs', label: 'Carbs', value: macros.carbPct.value, className: 'macro-seg-carbs', color: 'var(--series-carbs)' },
    { key: 'fat', label: 'Fat', value: macros.fatPct.value, className: 'macro-seg-fat', color: 'var(--series-fat)' },
  ];

  const known = segments.filter((s) => s.value !== null);
  if (known.length === 0) {
    return (
      <p className="na" style={{ fontSize: '0.78rem', margin: 0 }}>
        Macro breakdown unavailable —{' '}
        {macros.proteinPct.unavailableReason ?? 'nutrition data is missing.'}
      </p>
    );
  }

  const accounted = macros.accountedPct ?? 0;
  const remainder = Math.max(0, 100 - accounted);

  return (
    <div className="macro">
      <div
        className="macro-track"
        role="img"
        aria-label={`Macronutrient split of total calories: ${known
          .map((s) => `${s.label} ${formatPercent(s.value)}`)
          .join(', ')}${macros.complete ? '' : '. Breakdown is incomplete.'}`}
      >
        {known.map((s) => (
          <div
            key={s.key}
            className={`macro-seg ${s.className}`}
            style={{ width: `${Math.max(0, Math.min(100, s.value!))}%` }}
          />
        ))}
        {remainder > 0.5 && (
          <div
            className="macro-seg macro-seg-rest"
            style={{ width: `${remainder}%` }}
            title={
              macros.complete
                ? 'Not attributable to protein, carbohydrate or fat — typically fibre, sugar alcohols, or label rounding.'
                : 'Unknown — at least one macronutrient is not listed.'
            }
          />
        )}
      </div>

      {!compact && (
        <div className="macro-legend">
          {segments.map((s) => (
            <span key={s.key} className="macro-legend-item">
              <span className="macro-swatch" style={{ background: s.color }} aria-hidden="true" />
              {s.label}{' '}
              <span className="macro-legend-value">
                {s.value === null ? 'N/A' : formatPercent(s.value)}
              </span>
            </span>
          ))}
        </div>
      )}

      {!macros.complete && (
        <p className="macro-incomplete">
          Incomplete: {formatPercent(accounted)} of calories accounted for. Missing macros are not
          shown as zero.
        </p>
      )}
      {macros.complete && macros.energyOrigin === 'atwater' && (
        <p className="macro-incomplete">
          Calories computed from macronutrients (4/4/9), as no labelled value was available.
        </p>
      )}
    </div>
  );
}
